import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  ConversationThread,
  InboxResponse,
  NotificationSettings
} from 'types/interfaces/message';

export const messageApi = createApi({
  reducerPath: 'messageApi',
  baseQuery: baseQuery,
  tagTypes: ['Messages', 'UnreadCount', 'NotificationSettings'],
  endpoints: (builder) => ({
    sendMessage: builder.mutation<
      { success: boolean; messageId: string; conversationId: string },
      { receiverId: number; content: string }
    >({
      query: (body) => ({
        url: '/messages/send',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    getInbox: builder.query<InboxResponse, { page?: number; pageSize?: number }>({
      query: ({ page = 1, pageSize = 25 }) => ({
        url: `/messages/inbox?page=${page}&pageSize=${pageSize}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Messages'],
    }),

    getConversationThread: builder.query<ConversationThread, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Messages'],
    }),

    replyToMessage: builder.mutation<
      { success: boolean; messageId: string },
      { conversationId: string; content: string }
    >({
      query: ({ conversationId, content }) => ({
        url: `/messages/conversation/${conversationId}/reply`,
        method: 'POST',
        body: { content },
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    markAsRead: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}/read`,
        method: 'PUT',
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    archiveConversation: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}/archive`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages'],
    }),

    getUnreadCount: builder.query<{ count: number }, void>({
      query: () => ({
        url: '/messages/unread-count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['UnreadCount'],
    }),

    getNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => ({
        url: '/settings/notifications',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['NotificationSettings'],
    }),

    updateNotificationSettings: builder.mutation<
      { success: boolean; settings: NotificationSettings },
      { emailOnNewMessage?: boolean; emailOnNewTrade?: boolean }
    >({
      query: (body) => ({
        url: '/settings/notifications',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['NotificationSettings'],
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetInboxQuery,
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
  useArchiveConversationMutation,
  useGetUnreadCountQuery,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} = messageApi;
